import React from 'react'
import { observer } from 'mobx-react'
import { i18nTranslate } from 'src/utils'
import { Container, Button } from 'react-bootstrap'
import { cartContainer, catalogContainer } from 'src/models'
import { catalogDeps, productDeps } from 'src/deps'
import { checkEnableButton, isToShowOverrideSubscription } from 'src/utils'
import RelatedProductCarousel from './RelatedProductCarousel'
import './styles.scss'
import LeftArrowIcon from '../../Icons/LeftArrowIcon'
import RightArrowIcon from '../../Icons/RightArrowIcon'
import CustomArrowIcon from '../../Icons/CustomArrowIcon'

@observer
class RelatedProduct extends React.Component {
  carouselRef = React.createRef()

  state = {
    currentSlide: 0,
    isAtStart: true,
    isAtEnd: false,
  }

  componentDidMount() {
    this.updateArrowStates()
    window.addEventListener('resize', this.handleResize)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.products !== this.props.products) {
      this.updateArrowStates()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.updateArrowStates()
    }, 100)
  }

  updateArrowStates = () => {
    const products = this.props.products || []
    const effectiveSlidesToShow = this.getCurrentSlidesToShow()
    const maxSlide = Math.max(0, products.length - effectiveSlidesToShow)
    const isMobile = this.isMobileView()
    if (isMobile && products.length > 1) {
      this.setState({
        currentSlide: 0,
        isAtStart: false,
        isAtEnd: false,
      })
    } else {
      this.setState({
        currentSlide: 0,
        isAtStart: true,
        isAtEnd: products.length <= effectiveSlidesToShow || maxSlide === 0,
      })
    }
  }

  setSubscriptionData = (
    postData,
    isSubscriptionAvailable,
    isSubscriptionOverridePage
  ) => {
    if (isSubscriptionOverridePage && isSubscriptionAvailable) {
      postData.subscription = {
        frequencyPeriod: '1',
        frequencyType: 'MONTHS',
        type: 'VARIABLE_MULTI_ORDER',
      }
      postData.isSubscription = true
    }
  }

  processMandatoryProducts = (
    mandatoryProducts,
    postData,
    isPbomProduct,
    isPbomBackOrderedProduct
  ) => {
    let isBundleBackOrdered = false
    mandatoryProducts.forEach(mandatoryProduct => {
      const defaultSku =
        catalogDeps.getDefaultSelectedSku(mandatoryProduct)?.[0] || {}
      postData['skus'].push({
        productId: mandatoryProduct?.identifier || '',
        skuId: defaultSku?.identifier || '',
        type: 'MANDATORY',
        skuQuantity: mandatoryProduct?.properties?.quantity || 1,
        availableChannels: defaultSku?.properties?.availableChannels || '',
        backOrdered: isPbomProduct
          ? isPbomBackOrderedProduct
          : defaultSku?.inventoryProperties?.backOrdered || false,
      })
      if (!isBundleBackOrdered && defaultSku?.inventoryProperties?.backOrdered)
        isBundleBackOrdered = true
    })
    return isBundleBackOrdered
  }

  processBundleProduct = async (
    product,
    postData,
    isPbomProduct,
    isPbomBackOrderedProduct,
    isSubscriptionOverridePage
  ) => {
    const response = await catalogContainer.getProduct({
      productId: product?.identifier,
    })
    const {
      bundleMandatoryProducts = [],
      skuKit = [],
      identifier = '',
      type = 'DEFAULT',
      properties = {},
      prices = {},
      sku = [],
    } = response?.product?.[0] || product || {}
    const {
      availableChannels = '',
      scanQualifiedCount = 0,
      name = '',
      slug = '',
    } = properties || {}
    const isSubscriptionAvailable =
      checkEnableButton(availableChannels, 'subscription') || false
    const productType = type == 'kit' ? skuKit?.[0]?.type : type || 'DEFAULT'

    this.setSubscriptionData(
      postData,
      isSubscriptionAvailable,
      isSubscriptionOverridePage
    )

    postData['skus'] = [
      {
        productId: identifier,
        skuId: identifier,
        type: productType?.toUpperCase(),
        availableChannels,
      },
    ]

    const mandatoryProducts =
      type == 'bundle' ? bundleMandatoryProducts : skuKit
    const bundleSkuBackOrdered =
      sku?.[0]?.inventoryProperties?.backOrdered || false
    let isBundleBackOrdered = bundleSkuBackOrdered
    isBundleBackOrdered =
      this.processMandatoryProducts(
        mandatoryProducts,
        postData,
        isPbomProduct,
        isPbomBackOrderedProduct
      ) || isBundleBackOrdered

    postData.categoryId =
      catalogContainer?.categoryListName?.pdp?.categoryId ||
      response?.category?.identifier ||
      ''
    postData.listName = catalogContainer?.categoryListName?.pdp?.listName || ''
    postData.slug = slug
    postData.scanQualifiedCount = Number(scanQualifiedCount || 0)
    postData.isBackOrdered = isPbomProduct
      ? isPbomBackOrderedProduct
      : isBundleBackOrdered
    postData.name = name
    postData.productId = identifier
    postData.price = prices?.[0]?.price || ''
  }

  processRegularProduct = (product, postData, isSubscriptionOverridePage) => {
    const { availableChannels } = product?.sku?.[0]?.properties || {}
    const isSubscriptionAvailable =
      checkEnableButton(availableChannels, 'subscription') || false

    postData['skus'] = [
      {
        skuId: product?.sku?.[0]?.identifier || '',
        productId: product?.identifier || '',
        type: product?.properties?.producttype || 'DEFAULT',
      },
    ]
    postData.categoryId =
      catalogContainer?.categoryListName?.pdp?.categoryId ||
      (product?.properties?.productCategory || '').split(',')?.[0] ||
      ''
    postData.listName = catalogContainer?.categoryListName?.pdp?.listName || ''
    postData.slug = product?.properties?.slug
    postData.scanQualifiedCount =
      product?.sku?.[0]?.properties?.scanQualifiedCount || 0
    postData.isBackOrdered =
      product?.sku?.[0]?.inventoryProperties?.backOrdered || false
    postData.price = product?.sku?.[0]?.prices?.[0]?.price || ''

    this.setSubscriptionData(
      postData,
      isSubscriptionAvailable,
      isSubscriptionOverridePage
    )
  }

  handleAddToCart = async product => {
    const postData = { quantity: 1 }
    const isPbomProduct = productDeps.isPbomProduct(product)
    const isPbomBackOrderedProduct = product?.inventoryProperties?.backOrdered
    const isSubscriptionOverridePage = isToShowOverrideSubscription() || false

    if (productDeps.isBundleData(product)) {
      await this.processBundleProduct(
        product,
        postData,
        isPbomProduct,
        isPbomBackOrderedProduct,
        isSubscriptionOverridePage
      )
    } else {
      this.processRegularProduct(product, postData, isSubscriptionOverridePage)
    }

    if (isPbomProduct) {
      postData.isPbomProduct = isPbomProduct
    }

    await cartContainer.addToCart({
      cartArray: [postData],
      homePageAddToCart: true,
      isShowToast: true,
    })
  }

  getCurrentSlidesToShow = () => {
    const products = this.props.products || []
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
    let slidesToShow
    switch (true) {
      case windowWidth <= 420:
      case windowWidth <= 480:
        slidesToShow = 1
        break
      case windowWidth <= 768:
        slidesToShow = 2
        break
      case windowWidth <= 1024:
        slidesToShow = 4
        break
      default:
        slidesToShow = 4
    }
    return Math.min(slidesToShow, products.length)
  }

  isMobileView = () => {
    return typeof window !== 'undefined' && window.innerWidth <= 480
  }

  handleSlideChange = current => {
    const products = this.props.products || []
    const effectiveSlidesToShow = this.getCurrentSlidesToShow()
    const maxSlide = Math.max(0, products.length - effectiveSlidesToShow)
    const isMobile = this.isMobileView()

    if (isMobile && products.length > 1) {
      this.setState({
        currentSlide: current,
        isAtStart: false,
        isAtEnd: false,
      })
    } else {
      this.setState({
        currentSlide: current,
        isAtStart: current === 0,
        isAtEnd: current >= maxSlide,
      })
    }
  }

  handlePrevClick = () => {
    if (this.carouselRef.current && !this.state.isAtStart) {
      this.carouselRef.current.slickPrev()
    }
  }

  handleNextClick = () => {
    if (this.carouselRef.current && !this.state.isAtEnd) {
      this.carouselRef.current.slickNext()
    }
  }

  render() {
    const titleClassName = 'related-title'
    const products = this.props.products || []
    const effectiveSlidesToShow = this.getCurrentSlidesToShow()
    const needsNavigation = products.length > effectiveSlidesToShow
    const isMobile = this.isMobileView()

    if (!products || products.length === 0) {
      return null
    }

    return (
      <React.Fragment>
        <Container className="related-products-container">
          <div className="section-header">
            <h2
              className={titleClassName}
              tabIndex={0}
              aria-label={i18nTranslate(
                'you_may_also_like',
                'You may also like',
                {
                  nameSpace: 'ssr-resource',
                }
              )}>
              {i18nTranslate('you_may_also_like', 'You may also like', {
                nameSpace: 'ssr-resource',
              })}
            </h2>
            {needsNavigation && (
              <div className="navigation-arrows">
                {isMobile ? (
                  <Button
                    variant="link"
                    className={`arrow-btn custom-btn ${this.state.isAtEnd ? 'disabled' : ''}`}
                    onClick={this.handleNextClick}
                    disabled={this.state.isAtEnd}
                    tabIndex={0}
                    aria-label={i18nTranslate(
                      'pdp.nextproducts',
                      'Next products'
                    )}>
                    <CustomArrowIcon
                      className="custom-arrow-icon"
                      width="40"
                      height="40"
                      data-testid="custom-arrow-icon"
                      aria-label={i18nTranslate(
                        'pdp.nextproducts',
                        'Next products'
                      )}
                    />
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="link"
                      className={`arrow-btn prev-btn ${this.state.isAtStart ? 'disabled' : ''}`}
                      onClick={this.handlePrevClick}
                      disabled={this.state.isAtStart}
                      aria-label="Previous products"
                      tabIndex={0}>
                      <LeftArrowIcon
                        className="left-arrow-icon"
                        width="40"
                        height="40"
                        data-testid="left-arrow-icon"
                        aria-label={i18nTranslate('pdp.prev', 'Previous')}
                      />
                    </Button>

                    <Button
                      variant="link"
                      className={`arrow-btn next-btn ${this.state.isAtEnd ? 'disabled' : ''}`}
                      onClick={this.handleNextClick}
                      disabled={this.state.isAtEnd}
                      aria-label="Next products"
                      tabIndex={0}>
                      <RightArrowIcon
                        className="right-arrow-icon"
                        width="40"
                        height="40"
                        data-testid="right-arrow-icon"
                        aria-label={i18nTranslate('pdp.next', 'Next')}
                      />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <RelatedProductCarousel
            ref={this.carouselRef}
            products={products}
            onAddToCart={this.handleAddToCart}
            onSlideChange={this.handleSlideChange}
            slidesToShow={4}
            slidesToScroll={1}
            autoplay={false}
            showArrows={false}
          />
        </Container>
      </React.Fragment>
    )
  }
}
export { RelatedProduct }
export default RelatedProduct
